import { useMemo } from "react";
import { SYSTEM_STATUS_DISPLAY_ORDER } from "@/domain/systemAnalysis";
import {
  NOT_RELEVANT,
  scoreFor,
  trendFor,
  type LocationHistory,
  type PeriodOption,
} from "@/domain/systemAnalysisTrend";
import { format } from "@/i18n/translator";
import { useI18n } from "@/i18n/useI18n";
import { ElementTrendCard } from "./ElementTrendCard";
import { statusLabelKey } from "./useSystemAnalysis";

interface SystemAnalysisOverTimeProps {
  history: LocationHistory;
  /** Null with a single year: the first-analysis view. */
  period: PeriodOption | null;
  locationName: string;
  /** Opens the analysis dialog for a new year; absent for users who can't create analyses. */
  onPlan?: (year: number) => void;
}

/** Experimental: every checkpoint's status over the last two or three years. */
export function SystemAnalysisOverTime({
  history,
  period,
  locationName,
  onPlan,
}: SystemAnalysisOverTimeProps) {
  const { t } = useI18n();
  const firstYear = !period;
  const years = period?.years ?? history.years.slice(-1);
  const startYear = years[0];
  const endYear = years.at(-1);

  const summary = useMemo(() => {
    const ids = history.domains.flatMap((domain) => domain.items.map((item) => item.id));
    const valuesFor = (year: number | undefined) =>
      ids.map((id) =>
        year === undefined ? NOT_RELEVANT : (history.statusByYear[year]?.[id] ?? NOT_RELEVANT),
      );
    const last = valuesFor(endYear);
    // Legend: every status drawn in any year of the period; unassessed last.
    const drawn = new Set(years.flatMap((year) => valuesFor(year).map((value) => value.source)));
    const legend = [...SYSTEM_STATUS_DISPLAY_ORDER, "unknown" as const].filter((status) =>
      drawn.has(status),
    );
    const trends = ids.map((id) =>
      trendFor(years.map((year) => history.statusByYear[year]?.[id] ?? NOT_RELEVANT)),
    );
    return {
      legend,
      improved: trends.filter((trend) => trend === "improved").length,
      worse: trends.filter((trend) => trend === "worse").length,
      startScore: scoreFor(valuesFor(startYear)),
      endScore: scoreFor(last),
    };
  }, [history, years, startYear, endYear]);

  if (endYear === undefined) {
    return <div className="system-summary">{t("systemOverTimeNoData")}</div>;
  }
  const percent = (score: number | null) => (score === null ? "–" : `${score}%`);
  const nextYear = endYear + 1;

  return (
    <>
      <div className="system-summary system-trend-summary">
        {summary.legend.map((status) => (
          <span key={status} className="system-summary-item">
            <span
              className={`system-status-dot ${status === "unknown" ? "system-trend-dot-unassessed" : `system-status-${status}`}`}
              aria-hidden="true"
            />
            <span>{t(status === "unknown" ? "systemNotAssessed" : statusLabelKey(status))}</span>
          </span>
        ))}
        <span className="system-trend-summary-right">
          {firstYear ? (
            <span>{t("systemFirstAnalysisNote")}</span>
          ) : (
            <>
              <span>{format(t("systemSince"), { year: startYear! })}</span>
              <strong className="system-trend-change-up">
                {`▲ ${format(t("systemImproved"), { count: summary.improved })}`}
              </strong>
              <strong className="system-trend-change-down">
                {`▼ ${format(t("systemWorse"), { count: summary.worse })}`}
              </strong>
            </>
          )}
          <span className="system-trend-divider" aria-hidden="true" />
          <span>
            {`${t("systemOverallScore")} `}
            {!firstYear && (
              <>
                <span className="system-trend-start-score">{percent(summary.startScore)}</span>
                <span className="system-trend-arrow"> → </span>
              </>
            )}
            <strong className="system-trend-end-score">{percent(summary.endScore)}</strong>
          </span>
        </span>
      </div>

      {firstYear && (
        <div className="system-trend-banner">
          <svg width={74} height={28} viewBox="0 0 74 28" aria-hidden="true">
            <circle cx={14} cy={14} r={10} fill="#3a9b48" stroke="#ffffff" strokeWidth={1.5} />
            <line
              x1={28}
              y1={14}
              x2={46}
              y2={14}
              stroke="#9aa6ab"
              strokeWidth={2.5}
              strokeDasharray="4 3"
              strokeLinecap="round"
            />
            <circle
              cx={60}
              cy={14}
              r={10}
              fill="#ffffff"
              stroke="#9aa6ab"
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
          </svg>
          <div className="system-trend-banner-text">
            <strong>
              {format(t("systemFirstAnalysisTitle"), { location: locationName, year: nextYear })}
            </strong>
            <span>{format(t("systemFirstAnalysisText"), { year: nextYear })}</span>
          </div>
          {onPlan && (
            <button
              type="button"
              className="system-analysis-create-btn system-trend-plan-btn"
              onClick={() => onPlan(nextYear)}
            >
              {format(t("systemPlanAnalysis"), { year: nextYear })}
            </button>
          )}
        </div>
      )}

      <div id="system-grid" className="system-grid">
        {history.domains.map((domain) => (
          <ElementTrendCard
            key={domain.id}
            domain={domain}
            years={years}
            statusByYear={history.statusByYear}
            mode={firstYear ? "firstYear" : "history"}
          />
        ))}
      </div>
    </>
  );
}
